import React, { useState, useEffect } from 'react';
import { Trophy, Heart, Users, Award, Star, Flag, Shield, Target, Zap, Calendar, MapPin, Camera, User as UserIcon } from 'lucide-react';
import { chiSiamo, ringraziamenti as ringraziamentiMock } from '../data/mock';
import { api } from '../api/client';

const iconMap = { Trophy, Heart, Users, Award, Star, Flag, Shield, Target, Zap, Calendar, MapPin, Camera };

export default function ChiSiamo() {
  // Carica tutti i contenuti dal backend (con fallback su mock)
  const [paragraphs, setParagraphs] = useState(chiSiamo.paragraphs);
  const [subtitle, setSubtitle] = useState(chiSiamo.subtitle);
  const [missione, setMissione] = useState(chiSiamo.mission.map((m) => ({ icon: m.icon, titolo: m.title, desc: m.desc })));
  const [comitatoList, setComitatoList] = useState([]);
  const [ring, setRing] = useState({
    autorita_html: '',
    partner_html: '',
    operativi_html: '',
    titolo_autorita: 'Autorità & Istituzioni',
    titolo_partner: 'Partner Principali',
    titolo_operativi: 'Staff Operativo',
  });

  // Fallback dal mock se il backend non risponde
  const mockToHtml = (arr) => (arr || []).map((s) => `<p>${s}</p>`).join('');

  useEffect(() => {
    let alive = true;
    api.getChiSiamo().then((d) => {
      if (!alive) return;
      if (d?.subtitle) setSubtitle(d.subtitle);
      if (d?.paragraphs?.length > 0) setParagraphs(d.paragraphs);
      if (d?.missione?.length > 0) setMissione(d.missione);
      if (d?.comitato?.membri?.length > 0) {
        setComitatoList(d.comitato.membri.map((m) => ({ role: m.ruolo || '', name: m.nome || '', logo: m.logo || '' })));
      }
      if (d?.ringraziamenti) {
        const r = d.ringraziamenti;
        setRing({
          autorita_html: r.autorita_html || mockToHtml(ringraziamentiMock.autorita),
          partner_html: r.partner_html || mockToHtml(ringraziamentiMock.partner),
          operativi_html: r.operativi_html || mockToHtml(ringraziamentiMock.operativi),
          titolo_autorita: r.titolo_autorita || 'Autorità & Istituzioni',
          titolo_partner: r.titolo_partner || 'Partner Principali',
          titolo_operativi: r.titolo_operativi || 'Staff Operativo',
        });
      } else {
        setRing((p) => ({
          ...p,
          autorita_html: mockToHtml(ringraziamentiMock.autorita),
          partner_html: mockToHtml(ringraziamentiMock.partner),
          operativi_html: mockToHtml(ringraziamentiMock.operativi),
        }));
      }
    }).catch(() => { /* fallback su mock */ });
    return () => { alive = false; };
  }, []);

  return (
    <div className="bg-navy">
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1602432141202-e8b683524997?w=1600&q=80"
            alt="stadio"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-dark via-navy/90 to-navy"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 lg:px-8 text-center">
          <h1 className="font-brand text-5xl md:text-7xl font-bold text-white mb-4">{chiSiamo.title}</h1>
          <div className="w-24 h-1 bg-pink-brand mx-auto mb-4 rounded"></div>
          <p className="font-display text-xl md:text-2xl text-white/80 uppercase tracking-widest">
            {subtitle}
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <div className="space-y-6">
            {paragraphs.map((p, i) => (
              <p key={i} className="text-white/85 text-lg leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-navy-dark">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title centered text-4xl">La Nostra Missione</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {missione.map((m, i) => {
              const Icon = iconMap[m.icon] || Trophy;
              return (
                <div key={i} className="glass-card p-7 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-pink-brand flex items-center justify-center">
                    <Icon size={28} className="text-white" />
                  </div>
                  <h3 className="text-white font-bold text-xl mb-2">{m.titolo || m.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comitato Organizzatore */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="section-title centered text-3xl">Comitato Organizzatore</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {comitatoList.map((m, i) => (
              <div key={i} className="glass-card p-5 flex items-start gap-4">
                {m.logo ? (
                  <img src={m.logo} alt={m.name} className="w-12 h-12 object-contain rounded-xl bg-white/5 border border-pink-brand p-1 flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-pink-brand/20 border border-pink-brand flex items-center justify-center flex-shrink-0">
                    <UserIcon size={20} className="text-pink-brand" />
                  </div>
                )}
                <div>
                  <div className="text-pink-brand text-xs font-bold uppercase tracking-widest mb-1">{m.role}</div>
                  <div className="text-white font-semibold leading-tight">{m.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tribute */}
      <section className="py-16 bg-navy-dark">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <div className="glass-card p-8 lg:p-12 text-center">
            <div className="inline-block bg-pink-brand/20 border border-pink-brand rounded-full px-4 py-1.5 mb-5">
              <span className="text-pink-brand font-bold text-xs uppercase tracking-widest">In memoria di</span>
            </div>
            <h2 className="font-brand text-4xl md:text-5xl font-bold text-white mb-4">Eugenio Abbro</h2>
            <p className="text-white/80 text-base leading-relaxed max-w-2xl mx-auto">
              Sindaco di Cava de&rsquo; Tirreni per oltre 25 anni, Eugenio Abbro si è prodigato per la partecipazione allo sport dei giovani cavesi. Il Memorial è il segno tangibile del suo impegno civico e della sua passione per i ragazzi e lo sport come strumento di crescita.
            </p>
          </div>
        </div>
      </section>

      {/* Ringraziamenti */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="section-title centered text-3xl">Si Ringrazia</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <Group title={ring.titolo_autorita} html={ring.autorita_html} />
            <Group title={ring.titolo_partner} html={ring.partner_html} />
            <Group title={ring.titolo_operativi} html={ring.operativi_html} />
          </div>
        </div>
      </section>
    </div>
  );
}

function Group({ title, html }) {
  return (
    <div className="glass-card p-6">
      <h3 className="text-pink-brand font-bold uppercase tracking-widest text-xs mb-4">{title}</h3>
      <div className="text-white/85 text-sm leading-relaxed rte-output" dangerouslySetInnerHTML={{ __html: html || '' }} />
    </div>
  );
}
